/**
 * React hooks for the admin panels (no React Query in this project).
 *
 * Each hook binds the Worker base URL + a fresh-ID-token provider, calls the
 * `admin/api.ts` client, and exposes `{ data, loading, error, ...actions }`.
 * Token fetching is injected (`getIdToken`) so tests drive it with a fake.
 *
 * Domain: AutoDieta only. Nutrition/diet domain exclusively — no fitness plan domain.
 */

import { useCallback, useEffect, useState } from "react";
import type { ProviderId } from "@autodieta/shared";
import {
  aiGetConfig,
  aiListModels,
  aiSetActive,
  aiSetKey,
  aiSetModel,
  aiTestProvider,
  adminListUsers,
  adminGetUser,
  adminGetAudit,
  type AdminApiOptions,
  type AiConfigView,
  type ModelInfo,
  type AdminUserSummary,
  type AdminUserDetail,
  type AdminAuditEntry,
} from "./api.js";

export interface AdminHooksDeps {
  workerUrl: string;
  /** Returns a fresh admin ID token, or null if unavailable. */
  getIdToken: () => Promise<string | null>;
  /** Injectable fetch for tests. */
  fetchImpl?: typeof fetch;
}

async function withOpts<T>(
  deps: AdminHooksDeps,
  run: (opts: AdminApiOptions) => Promise<T>,
): Promise<T> {
  const idToken = await deps.getIdToken();
  if (!idToken) throw new Error("No ID token available.");
  return run({
    workerUrl: deps.workerUrl,
    idToken,
    ...(deps.fetchImpl ? { fetchImpl: deps.fetchImpl } : {}),
  });
}

// ── AI config ─────────────────────────────────────────────────────────────────

export function useAIConfig(deps: AdminHooksDeps): {
  config: AiConfigView | null;
  loading: boolean;
  error: string | null;
  setKey: (provider: ProviderId, key: string) => Promise<{ ok: boolean; masked: string }>;
  setActive: (provider: ProviderId) => Promise<void>;
  setModel: (provider: ProviderId, model: string) => Promise<void>;
} {
  const [config, setConfig] = useState<AiConfigView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await withOpts(deps, (opts) => aiGetConfig(opts));
      setConfig(data);
    } catch {
      setError("No se pudo cargar la configuración de IA.");
    } finally {
      setLoading(false);
    }
  }, [deps]);

  useEffect(() => {
    void load();
  }, [load]);

  const setKey = useCallback(
    async (provider: ProviderId, key: string) => {
      const result = await withOpts(deps, (opts) => aiSetKey(opts, provider, key));
      await load();
      return result;
    },
    [deps, load],
  );

  const setActive = useCallback(
    async (provider: ProviderId) => {
      await withOpts(deps, (opts) => aiSetActive(opts, provider));
      await load();
    },
    [deps, load],
  );

  const setModel = useCallback(
    async (provider: ProviderId, model: string) => {
      await withOpts(deps, (opts) => aiSetModel(opts, provider, model));
      await load();
    },
    [deps, load],
  );

  return { config, loading, error, setKey, setActive, setModel };
}

// ── AI models ─────────────────────────────────────────────────────────────────

export function useAIModels(
  deps: AdminHooksDeps,
  provider: ProviderId,
): {
  models: ModelInfo[];
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
} {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await withOpts(deps, (opts) => aiListModels(opts, provider));
      setModels(data.models);
    } catch {
      setError("No se pudieron cargar los modelos.");
    } finally {
      setLoading(false);
    }
  }, [deps, provider]);

  return { models, loading, error, load };
}

// ── Test provider ─────────────────────────────────────────────────────────────

export function useTestProvider(deps: AdminHooksDeps): {
  testing: boolean;
  result: { ok: boolean; message: string } | null;
  test: (provider: ProviderId) => Promise<void>;
} {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const test = useCallback(
    async (provider: ProviderId) => {
      setTesting(true);
      setResult(null);
      try {
        const res = await withOpts(deps, (opts) => aiTestProvider(opts, provider));
        setResult(res);
      } catch {
        setResult({ ok: false, message: "Error al probar el proveedor." });
      } finally {
        setTesting(false);
      }
    },
    [deps],
  );

  return { testing, result, test };
}

// ── Users ─────────────────────────────────────────────────────────────────────

export function useAdminUsers(deps: AdminHooksDeps): {
  users: AdminUserSummary[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await withOpts(deps, (opts) => adminListUsers(opts));
      setUsers(data);
    } catch {
      setError("No se pudieron cargar los usuarios.");
    } finally {
      setLoading(false);
    }
  }, [deps]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { users, loading, error, reload };
}

export function useAdminUserDetail(
  deps: AdminHooksDeps,
  uid: string,
): {
  detail: AdminUserDetail | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await withOpts(deps, (opts) => adminGetUser(opts, uid));
      setDetail(data);
    } catch {
      setError("No se pudo cargar el usuario.");
    } finally {
      setLoading(false);
    }
  }, [deps, uid]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { detail, loading, error, reload };
}

// ── Audit ─────────────────────────────────────────────────────────────────────

export function useAdminAudit(deps: AdminHooksDeps): {
  entries: AdminAuditEntry[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [entries, setEntries] = useState<AdminAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await withOpts(deps, (opts) => adminGetAudit(opts));
      setEntries(data.entries);
    } catch {
      setError("No se pudo cargar el registro de auditoría.");
    } finally {
      setLoading(false);
    }
  }, [deps]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { entries, loading, error, reload };
}
